import React, { useState, useEffect } from "react";
import axios from "axios";
import baseUrl from "../api/utils";

const fmt = (n) => (n || n === 0) ? Number(n).toLocaleString("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2
}) : "0.00";

// ═══════════════════════════════════════════════════════════════════════════════
// LINK PAYMENT TO TXNS — shown when "LINK PAYMENT" is clicked inside the
// Add Payment-In modal. Lets the cashier pick which open bill(s) this
// receipt pays off, either manually or via AUTO LINK (oldest bill first).
// ═══════════════════════════════════════════════════════════════════════════════
const LinkPaymentModal = ({ customerId, partyName, receivedAmount, initialAllocations, onCancel, onDone }) => {
  const token = sessionStorage.getItem("token");

  const [rows, setRows] = useState([]); // { saleInvoiceId, invoicePrefix, invoiceNumber, invoiceDate, totalAmount, balanceAmount, linkedAmount, checked }
  const [loading, setLoading] = useState(true);
  const [autoLinking, setAutoLinking] = useState(false);

  // ── load this customer's open bills on mount ──
  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    axios.get(`${baseUrl}/getOpenBillsForCustomer`, {
      headers: { Authorization: token },
      params: { customerId },
    })
      .then((res) => {
        const preset = initialAllocations || [];
        const merged = (res.data || []).map((b) => {
          const prior = preset.find((p) => p.saleInvoiceId === b.saleInvoiceId);
          const linked = prior ? prior.linkedAmount : (b.linkedAmount || 0);
          return { ...b, linkedAmount: linked, checked: linked > 0 };
        });
        setRows(merged);
      })
      .catch((err) => console.error("Failed to load open bills", err))
      .finally(() => setLoading(false));
  }, [customerId]);

  const totalLinked = rows.reduce((s, r) => s + (parseFloat(r.linkedAmount) || 0), 0);
  const unusedAmount = (parseFloat(receivedAmount) || 0) - totalLinked;

  // ── AUTO LINK — ask backend for the oldest-first distribution, replace rows ──
  const handleAutoLink = async () => {
    setAutoLinking(true);
    try {
      const res = await axios.get(`${baseUrl}/autoLinkPayment`, {
        headers: { Authorization: token },
        params: { customerId, amount: parseFloat(receivedAmount) || 0 },
      });
      setRows((res.data || []).map((b) => ({ ...b, checked: (b.linkedAmount || 0) > 0 })));
    } catch (err) {
      console.error("Auto link failed", err);
    } finally {
      setAutoLinking(false);
    }
  };

  const handleReset = () => {
    setRows((prev) => prev.map((r) => ({ ...r, linkedAmount: 0, checked: false })));
  };

  const toggleRow = (saleInvoiceId, checked) => {
    setRows((prev) => {
      const alreadyLinked = prev
        .filter((r) => r.saleInvoiceId !== saleInvoiceId)
        .reduce((s, r) => s + (parseFloat(r.linkedAmount) || 0), 0);
      const remaining = Math.max((parseFloat(receivedAmount) || 0) - alreadyLinked, 0);

      return prev.map((r) => {
        if (r.saleInvoiceId !== saleInvoiceId) return r;
        const amt = checked ? Math.min(r.balanceAmount, remaining) : 0;
        return { ...r, checked, linkedAmount: amt };
      });
    });
  };

  const updateLinkedAmount = (saleInvoiceId, value) => {
    setRows((prev) => {
      const alreadyLinked = prev
        .filter((r) => r.saleInvoiceId !== saleInvoiceId)
        .reduce((s, r) => s + (parseFloat(r.linkedAmount) || 0), 0);
      const remaining = Math.max((parseFloat(receivedAmount) || 0) - alreadyLinked, 0);

      return prev.map((r) => {
        if (r.saleInvoiceId !== saleInvoiceId) return r;
        const amt = Math.min(parseFloat(value) || 0, r.balanceAmount, remaining);
        return { ...r, linkedAmount: amt, checked: amt > 0 };
      });
    });
  };

  const handleDone = () => {
    const allocations = rows
      .filter((r) => (parseFloat(r.linkedAmount) || 0) > 0)
      .map((r) => ({ saleInvoiceId: r.saleInvoiceId, linkedAmount: parseFloat(r.linkedAmount) }));
    onDone(allocations);
  };

  const cellStyle = { padding: "10px 12px", fontSize: "13px", borderBottom: "1px solid #f0f0f0" };
  const thStyle = { padding: "10px 12px", fontSize: "12px", fontWeight: "600", color: "#555", textAlign: "left", borderBottom: "1px solid #e0e0e0" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10001, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: "8px", width: "760px", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>

        {/* header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #eee" }}>
          <span style={{ fontWeight: "700", fontSize: "16px", color: "#222" }}>Link Payment to Txns</span>
          <span onClick={onCancel} style={{ cursor: "pointer", fontSize: "18px", color: "#888" }}>✕</span>
        </div>

        
                {/* party + received + auto link */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", padding: "16px 20px 0 20px" }}>
        <div>
            <div style={{ fontSize: "12px", color: "#888" }}>Party</div>
            <div style={{ fontSize: "14px", fontWeight: "600", color: "#222" }}>{partyName || "—"}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div>
            <div style={{ fontSize: "12px", color: "#1976d2", fontWeight: "600" }}>Received</div>
            <input type="text" value={fmt(receivedAmount)} readOnly
                style={{ width: "110px", padding: "6px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", textAlign: "right", background: "#f9f9f9" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
            <button onClick={handleAutoLink} disabled={autoLinking}
                style={{ padding: "8px 16px", background: "#1976d2", color: "#fff", border: "none", borderRadius: "4px", fontWeight: "700", fontSize: "13px", cursor: autoLinking ? "default" : "pointer", marginTop: "18px" }}>
                {autoLinking ? "Linking..." : "AUTO LINK"}
            </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
            <span onClick={handleReset} title="Reset" style={{ cursor: "pointer", color: "#1976d2", fontSize: "16px", marginTop: "18px" }}>
                <i className="fa-solid fa-rotate-right"></i>
            </span>
            </div>
        </div>
        </div>
        {/* transactions table */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {loading ? (
            <div style={{ color: "#888", fontSize: "13px", padding: "20px 0" }}>Loading open bills…</div>
          ) : rows.length === 0 ? (
            <div style={{ color: "#888", fontSize: "13px", padding: "20px 0", textAlign: "center" }}>
              No open transactions for this party.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: "36px" }}></th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Ref/Inv No.</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Total</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Balance</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Linked Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.saleInvoiceId}>
                    <td style={cellStyle}>
                      <input type="checkbox" checked={r.checked}
                        onChange={(e) => toggleRow(r.saleInvoiceId, e.target.checked)}
                        style={{ accentColor: "#1976d2", width: "15px", height: "15px" }} />
                    </td>
                    <td style={cellStyle}>{r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString("en-GB").replace(/\//g, "-") : "—"}</td>
                    <td style={cellStyle}>Sale</td>
                    <td style={cellStyle}>
                      {r.invoicePrefix && r.invoicePrefix !== "NONE" ? `${r.invoicePrefix}-` : ""}{r.invoiceNumber}
                    </td>
                    <td style={{ ...cellStyle, textAlign: "right" }}>{fmt(r.totalAmount)}</td>
                    <td style={{ ...cellStyle, textAlign: "right", color: "#c0392b", fontWeight: "600" }}>{fmt(r.balanceAmount)}</td>
                    <td style={{ ...cellStyle, textAlign: "right" }}>
                      <input type="number" value={r.linkedAmount || ""} min="0" max={r.balanceAmount}
                        onChange={(e) => updateLinkedAmount(r.saleInvoiceId, e.target.value)}
                        style={{ width: "100px", padding: "5px 8px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", textAlign: "right" }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderTop: "1px solid #eee" }}>
          <span style={{ fontSize: "13px", color: "#555" }}>
            Unused Amount : <b style={{ color: unusedAmount > 0 ? "#c0392b" : "#27ae60" }}>{fmt(unusedAmount)}</b>
          </span>
          <div style={{ display: "flex", gap: "10px" }}>
            <button onClick={onCancel}
              style={{ padding: "9px 22px", background: "#f0f0f0", border: "none", borderRadius: "4px", fontWeight: "600", fontSize: "13px", cursor: "pointer", color: "#555" }}>
              CANCEL
            </button>
            <button onClick={handleDone}
              style={{ padding: "9px 22px", background: "#1976d2", border: "none", borderRadius: "4px", fontWeight: "600", fontSize: "13px", cursor: "pointer", color: "#fff" }}>
              DONE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkPaymentModal;